import React from "react";

import { getProductByUUID } from "../../../api/products";
import { Link } from "../../../common/Link";
import { RatingGraphic } from "../../../common/RatingGraphic";
import { useMountEffect } from "../../../hooks/useMountEffect";
import { IWebPageURL } from "../../../models/nominals";
import { IReview } from "../../../models/reviews";
import { formatDateString } from "../../../utils/format";
import { DisclosureCopy } from "./DisclosureCopy";
import { ReviewRecommended } from "./ReviewRecommended";
import { ReviewSource } from "./ReviewSource";
import { ReviewUserInfo } from "./ReviewUserInfo";

const buildProductTitle = (
    review: IReview,
    enablePDPLinks: boolean,
    productLink: IWebPageURL | null,
): React.ReactNode => {
    const title = review.product_variant_display_name
        ? `${review.product_name} – ${review.product_variant_display_name}`
        : review.product_name;
    if (enablePDPLinks && productLink) {
        return (
            <Link
                className="customer-review__review-item__product-link"
                href={productLink}
            >
                {title}
            </Link>
        );
    }
    return <div>{title}</div>;
};

export const Review = (props: {
    review: IReview;
    showSideDate: boolean;
    enablePDPLinks: boolean;
    showRecommended: boolean;
    showVerboseUserInfo: boolean;
    starHasStroke?: boolean;
}): React.ReactNode => {
    const [productLink, setProductLink] = React.useState<IWebPageURL | null>(
        null,
    );

    useMountEffect(() => {
        const fetchProductLink = async () => {
            if (!props.review.product_uuid) {
                return;
            }
            const product = await getProductByUUID(props.review.product_uuid);
            if (product && product.link && product.show_in_site_search) {
                setProductLink(product.link);
            }
        };
        void fetchProductLink();
    });

    const formattedDate = formatDateString(
        props.review.created_datetime,
        "MMM d, yyyy",
    );
    const formattedReviewDate = props.showSideDate ? formattedDate : null;
    const content = props.review.highlighted
        ? props.review.highlighted
        : props.review.text;

    return (
        <div
            className="customer-review__review-item"
            role="group"
            aria-label="Product Review"
            tabIndex={0}
        >
            <div className="customer-review__review-item__star-rating">
                <div className="customer-review__review-item__stars">
                    <RatingGraphic
                        cardClass="product-reviews-filter"
                        cardSize="standard"
                        rating={props.review.rating}
                        starHasStroke={props.starHasStroke}
                    />
                </div>
                <div className="customer-review__review-item__product">
                    {buildProductTitle(
                        props.review,
                        props.enablePDPLinks,
                        productLink,
                    )}
                    <span className="customer-review__review-item__date customer-review__review-item__date--mobile">
                        {formattedReviewDate}
                    </span>
                </div>
                <ReviewSource review={props.review} />
                <DisclosureCopy review={props.review} />
            </div>
            <div className="customer-review__review-item__content">
                <div className="customer-review__review-item__header">
                    <h2 className="customer-review__review-item__title">
                        {props.review.headline}
                    </h2>
                    <div className="customer-review__review-item__date">
                        {formattedReviewDate}
                    </div>
                </div>
                <ReviewUserInfo
                    showVerboseUserInfo={props.showVerboseUserInfo}
                    user={props.review.name}
                    location={props.review.location}
                    formattedDate={formattedDate}
                />
                <div
                    className="customer-review__review-item__copy"
                    dangerouslySetInnerHTML={{ __html: content }}
                ></div>
                <ReviewRecommended
                    showRecommended={props.showRecommended}
                    recommended={props.review.is_recommended}
                />
            </div>
        </div>
    );
};
